/**
 * Which sample the configure screen shows and tests on: a dataset item, one
 * result of a past experiment, or an observation from a trace source. Its dataset (and agent)
 * also decide which paths the mapping rows suggest.
 */

import { useState } from "react";
import { useExperiments } from "@/features/experiments/hooks";
import { isFinished } from "@/features/experiments/status";
import type { SampleRef } from "./hooks";
import { NEW_QUERY, type TraceQuery } from "@/features/traces/trace-query";
import type { TraceRef } from "@/features/traces/types";

export type SampleSource = "dataset" | "result" | "trace";

/** Which observation to try on: a source and filters, then one observation
 *  (`observationId` "" is the trace's root). */
export interface TraceChoice {
  connectionId: string;
  filters: TraceQuery;
  traceId: string;
  observationId: string;
}

const NO_TRACE: TraceChoice = { connectionId: "", filters: NEW_QUERY, traceId: "", observationId: "" };

export function useSampleChoice() {
  const [source, setSource] = useState<SampleSource>("dataset");
  const [datasetId, setDatasetId] = useState("");
  const [itemId, setItemId] = useState("");
  const [experimentId, setExperimentId] = useState("");
  const [resultId, setResultId] = useState("");
  const [trace, setTrace] = useState<TraceChoice>(NO_TRACE);
  const experiments = (useExperiments().data ?? []).filter((e) => isFinished(e.status));
  const experiment = experiments.find((e) => e.id === experimentId);

  const ref: SampleRef =
    source === "trace"
      ? trace.traceId
        ? {
            connection_id: trace.connectionId,
            trace_id: trace.traceId,
            ...(trace.observationId && { observation_id: trace.observationId }),
          }
        : {}
      : source === "dataset"
        ? itemId
          ? { dataset_item_id: itemId }
          : {}
        : resultId
          ? { item_result_id: resultId }
          : {};

  return {
    source,
    setSource,
    datasetId,
    setDatasetId: (id: string) => (setDatasetId(id), setItemId("")),
    itemId,
    setItemId,
    experiments,
    experimentId,
    setExperimentId: (id: string) => (setExperimentId(id), setResultId("")),
    resultId,
    setResultId,
    trace,
    setTraceSource: (connectionId: string) => setTrace({ ...NO_TRACE, connectionId }),
    setFilters: (filters: TraceQuery) => setTrace((t) => ({ ...t, filters })),
    /** The observation picked (`null`: none, e.g. the filters changed). */
    pickTrace: (ref: TraceRef | null) =>
      setTrace((t) => ({ ...t, traceId: ref?.id ?? "", observationId: ref?.observation_id ?? "" })),
    ref,
    /** Whose paths to suggest. */
    pathsFor: {
      dataset_id: (source === "dataset" ? datasetId : experiment?.dataset?.id) || undefined,
      agent_id: source === "result" ? experiment?.agent?.id : undefined,
    },
  };
}

export type SampleChoice = ReturnType<typeof useSampleChoice>;
